import { Skeleton } from '@components/ui/skeleton'
import { cn } from '@/utils'

export const TRAY_SKELETON_COUNT = 6

type TrayLoadingSkeletonProps = {
  /** Accessible name for the busy region, e.g. "Loading photos". */
  label: string
  count?: number
  /** Overrides each placeholder tile's shape — defaults to the two-column thumbnail aspect. */
  itemClassName?: string
  /** 1 = one tile per row; 2 = compact two-column grid (default) — matches `TrayGrid`. */
  columns?: 1 | 2
  /** Wraps each tile in the bordered card shell a tray's thumbnails use (Backgrounds), so the real
   *  thumbnails swap in without a layout shift. */
  framed?: boolean
  /** Overrides the `framed` shell's classes (merged over the Backgrounds card), for a tray whose
   *  real thumbnail card has a different radius or background (Templates). */
  frameClassName?: string
}

export function TrayLoadingSkeleton({
  label,
  count = TRAY_SKELETON_COUNT,
  itemClassName,
  columns = 2,
  framed = false,
  frameClassName,
}: TrayLoadingSkeletonProps) {
  return (
    <div
      className={cn(
        'grid w-full min-w-0 gap-2 p-0.5 py-2',
        columns === 1 ? 'grid-cols-1' : 'grid-cols-2'
      )}
      aria-busy="true"
      aria-label={label}
    >
      {Array.from({ length: count }, (_, index) => {
        const tile = (
          <Skeleton
            key={index}
            className={cn('aspect-[130/97.5] w-full rounded-md', itemClassName)}
          />
        )

        return framed ? (
          <div
            key={index}
            className={cn(
              'flex w-full flex-col items-stretch rounded-lg border border-border bg-card p-1',
              frameClassName
            )}
            data-testid="tray-skeleton-frame"
          >
            {tile}
          </div>
        ) : (
          tile
        )
      })}
    </div>
  )
}
